import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Skeleton, SkeletonTitle } from '@/components/ui/Skeleton';
import { Textarea } from '@/components/ui/Textarea';
import { api } from '@/lib/api';
import type { Event } from '@eventflow/shared-types';
import { type UpdateEventInput, updateEventSchema } from '@eventflow/validation';
import { zodResolver } from '@hookform/resolvers/zod';
import { Clock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

const formatTimeForInput = (timeStr: string | null | undefined): string => {
  if (!timeStr) return '';
  // Si viene con segundos (HH:MM:SS), tomar solo HH:MM
  const parts = timeStr.split(':');
  if (parts.length >= 2) {
    const hh = parts[0].padStart(2, '0');
    const mm = parts[1].padStart(2, '0');
    return `${hh}:${mm}`;
  }
  return timeStr;
};

// Convierte fecha UTC (que viene del backend) a YYYY-MM-DD local para el input date
const formatDateForInput = (dateStr: string | undefined): string => {
  if (!dateStr) return '';
  // Crear fecha (interpreta UTC si termina en Z, que es lo estándar de Supabase)
  const d = new Date(dateStr);

  // Usar métodos locales (getFullYear, etc) para obtener la fecha en la zona horaria del usuario
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export default function EditEventPage() {
  const { id: eventId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<Event | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isDirty },
  } = useForm<UpdateEventInput>({
    resolver: zodResolver(updateEventSchema),
    mode: 'onChange', // Validar al cambiar para feedback inmediato, pero cuidado con pre-fill
  });

  useEffect(() => {
    if (!eventId) {
      navigate('/dashboard/events');
      return;
    }

    const fetchEvent = async () => {
      setIsLoading(true);
      setError('');
      try {
        const eventData = await api.get<Event>(`/events/${eventId}`);
        setEvent(eventData);
        // Poblar el formulario con los datos existentes
        // Mapear el valor existente del evento al formulario, validando contra opciones permitidas
        const allowed = [8, 40, 56, 120];
        const deadline =
          eventData.payment_confirmation_deadline_hours === null
            ? null
            : allowed.includes(Number(eventData.payment_confirmation_deadline_hours))
              ? Number(eventData.payment_confirmation_deadline_hours)
              : undefined;

        // Sanitizar horas para asegurar formato HH:mm (requerido por input time y schema Zod)
        const startTime = formatTimeForInput(eventData.event_start_time);
        const endTime = formatTimeForInput(eventData.event_end_time);

        console.log('Sanitized times for form:', { startTime, endTime, originalStart: eventData.event_start_time });

        reset({
          name: eventData.name,
          description: eventData.description || '',
          event_date: formatDateForInput(eventData.event_date),
          event_start_time: startTime,
          event_end_time: endTime,
          venue_name: eventData.venue_name,
          venue_address: eventData.venue_address || '',
          venue_image_url: eventData.venue_image_url || '',
          is_published: eventData.is_published,
          payment_confirmation_deadline_hours: deadline,
        });
      } catch (err) {
        setError('No se pudo cargar el evento para editar.');
        console.error('Error fetching event for edit:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvent();
  }, [eventId, navigate, reset]);

  const onSubmit: SubmitHandler<UpdateEventInput> = async (data) => {
    if (!eventId) return;
    setIsSubmitting(true);
    setError('');
    try {
      // Combinar event_date con event_start_time para crear timestamp ISO
      let eventDateISO = data.event_date;
      if (data.event_date && data.event_start_time) {
        // Combinar fecha + hora de inicio para el timestamp principal
        const combinedDateTime = `${data.event_date}T${data.event_start_time}:00`;
        eventDateISO = new Date(combinedDateTime).toISOString();
      } else if (data.event_date) {
        // Si solo hay fecha, usar mediodía como default
        eventDateISO = new Date(`${data.event_date}T12:00:00`).toISOString();
      }

      // Coerción segura del campo de límite de confirmación
      const payload: UpdateEventInput = {
        ...data,
        event_date: eventDateISO,
        event_start_time: data.event_start_time,
        event_end_time: data.event_end_time,
        payment_confirmation_deadline_hours:
          data.payment_confirmation_deadline_hours === undefined
            ? undefined
            : data.payment_confirmation_deadline_hours === null
              ? null
              : Number(data.payment_confirmation_deadline_hours),
      } as UpdateEventInput;
      await api.put(`/events/${eventId}`, payload);
      navigate('/dashboard/events');
    } catch (err) {
      const rawMessage = err instanceof Error ? err.message : '';
      // Mensaje claro cuando la BD no tiene la columna (cache de esquema de PostgREST)
      if (
        rawMessage.includes('payment_confirmation_deadline_hours') &&
        rawMessage.toLowerCase().includes('schema cache')
      ) {
        setError(
          "La base de datos no tiene la columna 'payment_confirmation_deadline_hours' en 'events'. " +
          "Para habilitarla: aplica la migración SQL 018_add_event_payment_deadline.sql en tu proyecto Supabase y refresca la caché de PostgREST (NOTIFY pgrst, 'reload schema'). Luego intenta guardar nuevamente."
        );
        console.warn(
          'Guardar evento: columna payment_confirmation_deadline_hours ausente en BD. Aplica migración 018 y recarga caché.'
        );
      } else {
        setError(rawMessage || 'Error al actualizar el evento.');
        console.error('Error updating event:', err);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <SkeletonTitle width="w-48" />
          <Skeleton className="h-10 w-32 rounded-lg" />
        </div>

        {/* Formulario skeleton */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
          {/* Campo nombre */}
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-10 w-full rounded-lg" />
          </div>

          {/* Campo descripción */}
          <div className="space-y-2">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-24 w-full rounded-lg" />
          </div>

          {/* Campos en grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-10 w-full rounded-lg" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-10 w-full rounded-lg" />
            </div>
          </div>

          {/* Más campos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-10 w-full rounded-lg" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-10 w-full rounded-lg" />
            </div>
          </div>

          {/* Campo imagen */}
          <div className="space-y-2">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-10 w-full rounded-lg" />
          </div>

          {/* Botones */}
          <div className="flex justify-end gap-4 pt-4 border-t">
            <Skeleton className="h-10 w-24 rounded-lg" />
            <Skeleton className="h-10 w-36 rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  if (error && !event) {
    return (
      <Card className="bg-red-50 border-red-200">
        <p className="text-red-700 mb-4">{error}</p>
        <Button variant="secondary" onClick={() => navigate('/dashboard/events')}>
          Volver a Mis Eventos
        </Button>
      </Card>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/events')}
          className="text-sm text-gray-600 hover:text-gray-900 mb-2 flex items-center gap-1"
        >
          ← Volver a Mis Eventos
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Editar Evento</h1>
        <p className="text-gray-600 mt-2">Modifica los detalles de tu evento.</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-800">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <div className="space-y-6">
            {/* Nombre del evento */}
            <Input
              label="Nombre del Evento"
              type="text"
              required
              error={errors.name?.message}
              {...register('name')}
            />

            {/* Descripción */}
            <Textarea
              label="Descripción"
              rows={4}
              error={errors.description?.message}
              {...register('description')}
            />

            {/* Fecha del evento */}
            <div>
              <label htmlFor="event_date" className="block text-sm font-medium text-gray-700 mb-1">
                Fecha del Evento *
              </label>
              <input
                id="event_date"
                type="date"
                {...register('event_date')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {errors.event_date && (
                <p className="text-red-600 text-sm mt-1">{errors.event_date.message}</p>
              )}
            </div>

            {/* Horarios del evento */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="event_start_time" className="block text-sm font-medium text-gray-700 mb-1">
                  Hora de Inicio *
                </label>
                <input
                  id="event_start_time"
                  type="time"
                  {...register('event_start_time')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {errors.event_start_time && (
                  <p className="text-red-600 text-sm mt-1">{errors.event_start_time.message}</p>
                )}
              </div>
              <div>
                <label htmlFor="event_end_time" className="block text-sm font-medium text-gray-700 mb-1">
                  Hora de Fin *
                </label>
                <input
                  id="event_end_time"
                  type="time"
                  {...register('event_end_time')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {errors.event_end_time && (
                  <p className="text-red-600 text-sm mt-1">{errors.event_end_time.message}</p>
                )}
              </div>
            </div>

            {/* Duración calculada */}
            {watch('event_start_time') && watch('event_end_time') && (() => {
              const start = watch('event_start_time');
              const end = watch('event_end_time');
              if (start && end) {
                const [startH, startM] = start.split(':').map(Number);
                const [endH, endM] = end.split(':').map(Number);
                const startMinutes = startH * 60 + startM;
                const endMinutes = endH * 60 + endM;
                const diff = endMinutes - startMinutes;
                if (diff > 0) {
                  const hours = Math.floor(diff / 60);
                  const minutes = diff % 60;
                  return (
                    <div className="flex items-center gap-2 text-sm text-indigo-600 font-medium bg-indigo-50 px-3 py-2 rounded-md border border-indigo-100">
                      <Clock className="w-4 h-4" />
                      <span>Duración: {hours}h {minutes}min</span>
                    </div>
                  );
                }
              }
              return null;
            })()}

            {/* Nombre del venue */}
            <Input
              label="Lugar del Evento (Venue)"
              type="text"
              required
              error={errors.venue_name?.message}
              {...register('venue_name')}
            />

            {/* Dirección del venue */}
            <Input
              label="Dirección"
              type="text"
              error={errors.venue_address?.message}
              {...register('venue_address')}
            />

            {/* URL de imagen del venue */}
            <Input
              label="URL de Imagen del Lugar"
              type="url"
              placeholder="https://ejemplo.com/imagen.jpg"
              error={errors.venue_image_url?.message}
              {...register('venue_image_url')}
            />

            {/* Publicado */}
            <div className="flex items-center gap-3">
              <input
                id="is_published"
                type="checkbox"
                className="form-checkbox"
                {...register('is_published')}
              />
              <label htmlFor="is_published" className="text-sm font-medium text-gray-700">
                Publicar evento (visible para clientes)
              </label>
            </div>

            {/* Límite de confirmación de pagos */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Límite de confirmación de pagos
              </label>
              <select
                className="w-full border rounded px-3 py-2"
                value={(() => {
                  const sel = watch('payment_confirmation_deadline_hours');
                  const allowed = [8, 40, 56, 120];
                  if (sel === null) return 'null';
                  if (sel === undefined) return '';
                  return allowed.includes(Number(sel)) ? String(sel) : '';
                })()}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '') {
                    setValue('payment_confirmation_deadline_hours', undefined, {
                      shouldDirty: true,
                    });
                  } else if (v === 'null') {
                    setValue('payment_confirmation_deadline_hours', null, {
                      shouldDirty: true,
                    });
                  } else {
                    setValue('payment_confirmation_deadline_hours', Number(v) as any, {
                      shouldDirty: true,
                    });
                  }
                }}
              >
                <option value="">Sin cambios</option>
                <option value="null">Sin límite</option>
                <option value={8}>1 día hábil (8h)</option>
                <option value={40}>5 días hábiles (40h)</option>
                <option value={56}>7 días hábiles (56h)</option>
                <option value={120}>15 días hábiles (120h)</option>
              </select>
              <p className="text-xs text-gray-600">
                Define en cuántas horas hábiles (L-V, 9:00-18:00 hora México) debe confirmarse el
                pago antes de cancelarse automáticamente.
                <br />
                <strong>"Sin límite":</strong> Los pagos pendientes permanecerán indefinidamente sin
                cancelarse automáticamente.
              </p>
              {errors.payment_confirmation_deadline_hours?.message && (
                <p className="text-sm text-red-600">
                  {errors.payment_confirmation_deadline_hours?.message}
                </p>
              )}
            </div>
          </div>

          {/* Botones */}
          <div className="flex gap-3 pt-6 mt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/dashboard/events')}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting || !isDirty}
              className="flex-1"
            >
              {isSubmitting ? 'Guardando...' : '💾 Guardar Cambios'}
            </Button>
          </div>
        </Card>
      </form>
    </div>
  );
}
